import {
  Button,
  Flex,
  Skeleton,
  Spacer,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Util } from '@finley-technologies/finley-types';

import { IsolatedMultiSelect } from 'packages/common-frontend/components/MultiSelect';
import {
  errorToastConfig,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { getLoanLabel } from 'packages/invoices/ui/utils';
import {
  GetBatchSettlementsForCreditFacilityDocumentGQLType,
  useCreateBatchSettlementMutation,
  useGetBatchSettlementsForCreditFacilityQuery,
  useGetSummarizedLoansQuery,
} from 'packages/loans/requests/loans.gql.generated';
import { SettlementReportGenerateSideDrawer } from 'packages/loans/ui/settlementReports/SettlementReportGenerateSideDrawer';
import { SettlementReportsTable } from 'packages/loans/ui/settlementReports/SettlementReportsTable';

export const SettlementReportsContainer = ({
  creditFacilityId,
}: {
  creditFacilityId: string;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const [selectedLenderIds, setSelectedLenderIds] = useState<string[]>([]);
  const [selectedLoanIds, setSelectedLoanIds] = useState<string[]>([]);
  const [batchSettlementWorkleases, setBatchSettlementWorkleases] = useState<
    { batchSettlementId: string; workleaseId?: string | null }[]
  >([]);

  const {
    data: summarizedLoansData,
    loading: isSummarizedLoansLoading,
    error: summarizedLoansError,
  } = useGetSummarizedLoansQuery({
    variables: {
      filterParams: {
        creditFacilityIds: [creditFacilityId],
        isFinal: true,
      },
    },
  });

  const {
    data: settlementReportsData,
    loading: isSettlementReportsLoading,
    error: settlementReportsError,
    refetch: refetchSettlementReports,
  } = useGetBatchSettlementsForCreditFacilityQuery({
    variables: {
      creditFacilityId,
      lenderIds: selectedLenderIds,
      loanIds: selectedLoanIds,
    },
  });

  const [
    createBatchSettlement,
    { loading: isCreateBatchSettlementLoading },
  ] = useCreateBatchSettlementMutation({
    onCompleted: (data) => {
      toast({
        ...successToastConfig,
        title: 'Generating settlement reports.',
      });

      setBatchSettlementWorkleases(data.createBatchSettlement);
    },
    onError: (error) => {
      Sentry.captureException(error);

      toast({
        ...errorToastConfig,
        title: 'Failed to generate settlement reports',
      });
    },
    refetchQueries: [GetBatchSettlementsForCreditFacilityDocumentGQLType],
    notifyOnNetworkStatusChange: true,
  });

  const onClickGenerate = useCallback(
    async (params: {
      lenderIds: string[];
      loanIds: string[];
      startDate: Util.FullDateStr;
      endDate: Util.FullDateStr;
    }) => {
      // reset the batch settlement workleases
      setBatchSettlementWorkleases([]);

      await createBatchSettlement({
        variables: {
          creditFacilityId,
          ...params,
        },
      });
    },
    [createBatchSettlement, creditFacilityId],
  );

  useEffect(() => {
    if (summarizedLoansData) {
      const loanOptions = summarizedLoansData.loans.map((loan) => loan.id);
      const lenderOptions = summarizedLoansData.loans
        .flatMap((loan) => loan.lenderOrganizations)
        .map((lender) => lender.id);

      setSelectedLoanIds([...new Set(loanOptions)]);
      setSelectedLenderIds([...new Set(lenderOptions)]);
    }
  }, [summarizedLoansData]);

  const allLoanAndLenderOptions = useMemo(() => {
    if (!summarizedLoansData) {
      return { loanOptions: [], lenderOptions: [] };
    }

    const loanOptions: { id: string; label: string; value: string }[] = [];
    const lenderOptions: Record<
      string,
      { id: string; label: string; value: string }
    > = {};

    summarizedLoansData.loans.forEach((loan) => {
      const loanOption = {
        id: loan.id,
        label: getLoanLabel({
          loanId: loan.id,
          loanName: loan.name,
          externalLoanId: null,
        }),
        value: loan.id,
      };

      loanOptions.push(loanOption);

      loan.lenderOrganizations.forEach((lender) => {
        if (!lenderOptions[lender.id]) {
          lenderOptions[lender.id] = {
            id: lender.id,
            label: lender.name,
            value: lender.id,
          };
        }
      });
    });

    const sortedLenderOptions = Object.values(lenderOptions).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
    const sortedLoanOptions = loanOptions.sort((a, b) =>
      a.label.localeCompare(b.label),
    );

    return {
      loanOptions: sortedLoanOptions,
      lenderOptions: sortedLenderOptions,
    };
  }, [summarizedLoansData]);

  if (summarizedLoansError) {
    throw summarizedLoansError;
  }
  if (settlementReportsError) {
    throw settlementReportsError;
  }

  if (isSummarizedLoansLoading || isSettlementReportsLoading) {
    return <Skeleton h="600px" />;
  }

  return (
    <>
      <Flex justifyContent="space-between" mb="2rem" gap="1rem">
        <IsolatedMultiSelect
          applyButtonText="Apply"
          buttonProps={{
            width: '15%',
          }}
          initialSelections={selectedLenderIds}
          isSearchable
          menuListProps={{ zIndex: 'dropdown' }}
          onApplyOptions={(lenderIds) => {
            setSelectedLenderIds(lenderIds);
          }}
          options={allLoanAndLenderOptions.lenderOptions}
          placeholderText="Select lenders"
          showSelectedCount
          titleText={`${
            selectedLenderIds.length ===
            allLoanAndLenderOptions.lenderOptions.length
              ? 'All Lenders'
              : 'Lenders'
          }`}
        />
        <IsolatedMultiSelect
          applyButtonText="Apply"
          buttonProps={{
            width: '15%',
          }}
          initialSelections={selectedLoanIds}
          isSearchable
          menuListProps={{ zIndex: 'dropdown' }}
          onApplyOptions={(loanIds) => {
            setSelectedLoanIds(loanIds);
          }}
          options={allLoanAndLenderOptions.loanOptions}
          placeholderText="Select loans"
          titleText={`${
            selectedLoanIds.length ===
            allLoanAndLenderOptions.loanOptions.length
              ? 'All Loans'
              : 'Loans'
          }`}
        />
        <Spacer />
        <Button onClick={onOpen} variant="secondary" colorScheme="v2-aurora">
          Generate Settlement Report
        </Button>
      </Flex>
      <SettlementReportsTable
        batchSettlementWorkleases={batchSettlementWorkleases}
        settlementReports={settlementReportsData?.settlements ?? []}
        refetchSettlementReports={refetchSettlementReports}
      />
      <SettlementReportGenerateSideDrawer
        isOpen={isOpen}
        onClose={onClose}
        loanOptions={allLoanAndLenderOptions.loanOptions}
        lenderOptions={allLoanAndLenderOptions.lenderOptions}
        onClickGenerate={onClickGenerate}
        isCreateBatchSettlementLoading={isCreateBatchSettlementLoading}
      />
    </>
  );
};
